import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { casesAPI, statusAPI, documentsAPI, clientPortalsAPI, customFieldsAPI } from '../lib/api';
import { useDropzone } from 'react-dropzone';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { format } from 'date-fns';
import { 
  Loader2, ArrowLeft, CalendarIcon, FileText, Clock, Download, Trash2, Upload,
  Plus, Save, User, Phone, Mail, MapPin, Edit2, Link2, Copy, RefreshCw,
  ExternalLink, Key, CheckCircle, Globe, Briefcase, GraduationCap, Heart,
  Plane, Fingerprint, BookOpen, ScanLine, Crown,
} from 'lucide-react';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import EnhancedFormScanner from '../components/EnhancedFormScanner';
import AddCustomFieldDialog from '../components/AddCustomFieldDialog';
import { cn } from '../lib/utils';
import { CountyAutocomplete } from '../components/CountyAutocomplete';
import { US_STATE_OPTIONS } from '../lib/us-counties';

// Reusable field component
function F({ label, value, isEditing, field, editData, onChange, type = 'text', options = [], testId }) {
  const tid = testId || `edit-${field}`;
  if (isEditing) {
    if (type === 'select') {
      return (
        <div>
          <p className="font-body text-xs text-muted-foreground mb-1">{label}</p>
          <Select value={editData[field] || ''} onValueChange={(v) => onChange(field, v)}>
            <SelectTrigger className="font-body text-sm h-9" data-testid={tid}><SelectValue placeholder={`Select...`} /></SelectTrigger>
            <SelectContent>{options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      );
    }
    if (type === 'date') {
      const rawVal = editData[field];
      let parsedDate = null;
      let displayText = '';
      if (rawVal) {
        const cleaned = String(rawVal).replace(/(\d+)(st|nd|rd|th)/g, '$1');
        const d = new Date(cleaned);
        if (!isNaN(d.getTime())) {
          parsedDate = d;
          try { displayText = format(d, 'PPP'); } catch { displayText = rawVal; }
        } else {
          displayText = rawVal;
        }
      }
      return (
        <div>
          <p className="font-body text-xs text-muted-foreground mb-1">{label}</p>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className={cn("w-full justify-start text-left font-body text-sm h-9", !rawVal && "text-muted-foreground")} data-testid={tid}>
                <CalendarIcon className="mr-2 h-3 w-3" />
                {rawVal ? displayText : <span>Pick a date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={parsedDate || undefined} onSelect={(d) => onChange(field, d ? format(d, 'yyyy-MM-dd') : '')} initialFocus />
            </PopoverContent>
          </Popover>
        </div>
      );
    }
    if (type === 'textarea') {
      return (
        <div className="col-span-full">
          <p className="font-body text-xs text-muted-foreground mb-1">{label}</p>
          <Textarea value={editData[field] || ''} onChange={(e) => onChange(field, e.target.value)} className="font-body text-sm" rows={2} data-testid={tid} />
        </div>
      );
    }
    return (
      <div>
        <p className="font-body text-xs text-muted-foreground mb-1">{label}</p>
        <Input value={editData[field] || ''} onChange={(e) => onChange(field, e.target.value)} className="font-body text-sm h-9" data-testid={tid} />
      </div>
    );
  }
  return (
    <div data-testid={`field-${field}`}>
      <p className="font-body text-xs text-muted-foreground">{label}</p>
      <p className="font-body text-sm font-medium text-foreground">{value || '-'}</p>
    </div>
  );
}

// Reusable list editor for repeating groups (passports, visas, etc.)
function ListSection({ title, items, fields, isEditing, listKey, editData, onChange, icon: Icon }) {
  const list = isEditing ? (editData[listKey] || []) : (items || []);
  const addItem = () => {
    const blank = {};
    fields.forEach(f => { blank[f.key] = ''; });
    onChange(listKey, [...(editData[listKey] || []), blank]);
  };
  const removeItem = (idx) => {
    const updated = [...(editData[listKey] || [])];
    updated.splice(idx, 1);
    onChange(listKey, updated);
  };
  const updateItem = (idx, key, val) => {
    const updated = [...(editData[listKey] || [])];
    updated[idx] = { ...updated[idx], [key]: val };
    onChange(listKey, updated);
  };

  return (
    <Card data-testid={`list-${listKey}`}>
      <CardHeader className="pb-3">
        <CardTitle className="font-heading text-base flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-primary" />}
          {title}
          {isEditing && (
            <Button variant="outline" size="sm" className="ml-auto h-7 text-xs" onClick={addItem} data-testid={`add-${listKey}`}>
              <Plus className="w-3 h-3 mr-1" /> Add
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {list.length === 0 ? (
          <p className="font-body text-sm text-muted-foreground text-center py-4">No entries</p>
        ) : (
          <div className="space-y-3">
            {list.map((item, idx) => (
              <div key={idx} className="p-3 rounded-lg border border-border bg-card">
                {isEditing && (
                  <div className="flex justify-end mb-2">
                    <Button variant="ghost" size="sm" className="h-6 text-xs text-error" onClick={() => removeItem(idx)}>
                      <Trash2 className="w-3 h-3 mr-1" /> Remove
                    </Button>
                  </div>
                )}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {fields.map(f => (
                    <div key={f.key}>
                      <p className="font-body text-xs text-muted-foreground mb-1">{f.label}</p>
                      {isEditing ? (
                        <Input value={item[f.key] || ''} onChange={(e) => updateItem(idx, f.key, e.target.value)} className="font-body text-sm h-8" />
                      ) : (
                        <p className="font-body text-sm font-medium">{item[f.key] || '-'}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function CaseDetailPage() {
  const { id } = useParams();
  const { user, hasFeature } = useAuth();
  const navigate = useNavigate();
  const [caseItem, setCase] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [statusUpdates, setStatusUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [statusDialog, setStatusDialog] = useState(false);
  const [newStatus, setNewStatus] = useState({ status: '', notes: '' });
  const [clientPortal, setClientPortal] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [portalDialog, setPortalDialog] = useState(false);
  const [portalEmail, setPortalEmail] = useState('');
  const [newAccessCode, setNewAccessCode] = useState(null);
  const [customFields, setCustomFields] = useState([]);

  useEffect(() => { fetchData(); fetchCustomFields(); }, [id]);

  const fetchData = async () => {
    try {
      const [caseItemRes, docsRes, statusRes] = await Promise.all([
        casesAPI.getOne(id), documentsAPI.getAll(id), statusAPI.getUpdates(id)
      ]);
      setCase(caseItemRes.data);
      setEditData(caseItemRes.data);
      setDocuments(docsRes.data);
      setStatusUpdates(statusRes.data);
      try {
        const portalRes = await clientPortalsAPI.getForCase(id);
        setClientPortal(portalRes.data);
      } catch { setClientPortal(null); }
    } catch {
      toast.error('Failed to load case data');
      navigate('/cases');
    } finally { setLoading(false); }
  };

  const fetchCustomFields = async () => {
    try {
      const res = await customFieldsAPI.list();
      setCustomFields(res.data);
    } catch { /* silent */ }
  };

  const handleEditChange = (field, value) => setEditData(prev => ({ ...prev, [field]: value }));

  const handleCustomFieldChange = (key, value) => {
    setEditData(prev => ({
      ...prev,
      custom_fields: { ...(prev.custom_fields || {}), [key]: value }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await casesAPI.update(id, editData);
      setCase(response.data);
      setIsEditing(false);
      toast.success('Case updated successfully');
    } catch { toast.error('Failed to update case'); }
    finally { setSaving(false); }
  };

  const handleAddStatus = async () => {
    if (!newStatus.status) { toast.error('Please select a status'); return; }
    try {
      const response = await statusAPI.createUpdate(id, newStatus);
      setStatusUpdates([response.data, ...statusUpdates]);
      setCase(prev => ({ ...prev, status: newStatus.status }));
      setStatusDialog(false);
      setNewStatus({ status: '', notes: '' });
      toast.success('Status updated');
    } catch { toast.error('Failed to update status'); }
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    setUploading(true);
    try {
      for (const file of acceptedFiles) {
        const response = await documentsAPI.upload(id, file);
        setDocuments(prev => [response.data, ...prev]);
      }
      toast.success(`${acceptedFiles.length} file(s) uploaded`);
    } catch { toast.error('Failed to upload file(s)'); }
    finally { setUploading(false); }
  }, [id]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const handleDownload = async (doc) => {
    try {
      const response = await documentsAPI.download(doc.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch { toast.error('Failed to download file'); }
  };

  const handleDeleteDoc = async (docId) => {
    try {
      await documentsAPI.delete(docId);
      setDocuments(documents.filter(d => d.id !== docId));
      toast.success('Document deleted');
    } catch { toast.error('Failed to delete document'); }
  };

  const handleScanData = (data) => {
    const mapped = {};
    // Auto-split full name into parts if individual parts not provided
    if (data.name) mapped.name = data.name;
    if (data.first_name) mapped.first_name = data.first_name;
    if (data.middle_name) mapped.middle_name = data.middle_name;
    if (data.last_name) mapped.last_name = data.last_name;
    if (!data.first_name && data.name) {
      const parts = data.name.trim().split(/\s+/);
      mapped.first_name = parts[0] || '';
      mapped.last_name = parts.length > 1 ? parts[parts.length - 1] : '';
      if (parts.length > 2) mapped.middle_name = parts.slice(1, -1).join(' ');
    }
    if (data.maiden_name) mapped.maiden_name = data.maiden_name;
    if (data.title) mapped.title = data.title;
    if (data.sex) mapped.sex = data.sex;
    if (data.marital_status) mapped.marital_status = data.marital_status;
    if (data.dob) mapped.dob = data.dob;
    if (data.city_of_birth) mapped.city_of_birth = data.city_of_birth;
    if (data.state_of_birth) mapped.state_of_birth = data.state_of_birth;
    if (data.country_of_birth) mapped.country_of_birth = data.country_of_birth;
    if (data.citizenship) mapped.citizenship = data.citizenship;
    if (data.ssn) mapped.ssn = data.ssn;
    if (data.a_number) mapped.a_number = data.a_number;
    if (data.email) mapped.email = data.email;
    if (data.telephone) mapped.telephone = data.telephone;
    if (data.day_phone) mapped.day_phone = data.day_phone;
    if (data.cell_phone) mapped.cell_phone = data.cell_phone;
    if (data.address) mapped.address = data.address;
    if (data.mailing_address) mapped.mailing_address = data.mailing_address;
    if (data.country) mapped.country = data.country;
    if (data.type_of_service) mapped.type_of_services = data.type_of_service;
    if (data.uscis_receipt) mapped.uscis_receipt = data.uscis_receipt;
    if (data.date_of_arrival) mapped.date_of_arrival = data.date_of_arrival;
    if (data.nonimmigrant_status) mapped.nonimmigrant_status = data.nonimmigrant_status;
    if (data.employer_name) mapped.employer_name = data.employer_name;
    if (data.job_title) mapped.job_title = data.job_title;
    if (data.employer_address) mapped.employer_address = data.employer_address;
    if (data.emergency_contact_name) mapped.emergency_contact_name = data.emergency_contact_name;
    if (data.emergency_contact_phone) mapped.emergency_contact_phone = data.emergency_contact_phone;
    if (data.ethnicity) mapped.ethnicity = data.ethnicity;
    if (data.race) mapped.race = data.race;
    if (data.height) mapped.height = data.height;
    if (data.weight) mapped.weight = data.weight;
    if (data.eye_color) mapped.eye_color = data.eye_color;
    if (data.hair_color) mapped.hair_color = data.hair_color;
    // List fields
    if (Array.isArray(data.previous_addresses) && data.previous_addresses.length > 0) mapped.previous_addresses = data.previous_addresses;
    if (Array.isArray(data.education) && data.education.length > 0) mapped.education = data.education;
    if (Array.isArray(data.marriages) && data.marriages.length > 0) mapped.marriages = data.marriages;
    if (Array.isArray(data.abroad_trips) && data.abroad_trips.length > 0) mapped.abroad_trips = data.abroad_trips;
    if (Array.isArray(data.previous_employment) && data.previous_employment.length > 0) mapped.previous_employment = data.previous_employment;
    setEditData(prev => ({ ...prev, ...mapped }));
    if (!isEditing) setIsEditing(true);
  };

  // Portal handlers
  const handleCreatePortal = async () => {
    if (!portalEmail) { toast.error('Please enter client email'); return; }
    setPortalLoading(true);
    try {
      const response = await clientPortalsAPI.create({ case_id: id, email: portalEmail });
      setClientPortal(response.data);
      setNewAccessCode(response.data.access_code);
      setPortalDialog(false);
      toast.success('Client portal created!');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create portal'); }
    finally { setPortalLoading(false); }
  };

  const handleRegenerateCode = async () => {
    if (!clientPortal?.id) return;
    setPortalLoading(true);
    try {
      const response = await clientPortalsAPI.regenerateCode(clientPortal.id);
      setNewAccessCode(response.data.access_code);
      toast.success('New access code generated!');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to regenerate code'); }
    finally { setPortalLoading(false); }
  };

  const handleRevokePortal = async () => {
    if (!clientPortal?.id) return;
    if (!window.confirm('Are you sure you want to revoke portal access?')) return;
    setPortalLoading(true);
    try {
      await clientPortalsAPI.revoke(clientPortal.id);
      setClientPortal(null);
      setNewAccessCode(null);
      toast.success('Portal access revoked');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to revoke portal'); }
    finally { setPortalLoading(false); }
  };

  const copyToClipboard = (text) => { navigator.clipboard.writeText(text); toast.success('Copied!'); };

  const getStatusColor = (status) => {
    const c = { 'Active': 'bg-success text-white', 'Pending': 'bg-warning text-white', 'Approved': 'bg-primary text-white', 'Denied': 'bg-error text-white' };
    return c[status] || 'bg-muted text-foreground';
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />
      <main className="ml-0 md:ml-64 p-4 md:p-8 pt-16 md:pt-8">
        {/* Header */}
        <div className="mb-6">
          <Link to="/cases" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground font-body text-sm mb-4 transition-colors" data-testid="back-to-cases">
            <ArrowLeft className="w-4 h-4" /> Back to Cases
          </Link>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="font-heading text-xl font-bold text-primary">{caseItem?.name?.charAt(0)?.toUpperCase() || '?'}</span>
              </div>
              <div>
                <h1 className="font-heading text-2xl font-bold text-foreground tracking-tight">{caseItem?.name}</h1>
                <div className="flex items-center gap-3 mt-1">
                  <span className={`px-3 py-1 rounded-full text-xs font-body font-medium ${getStatusColor(caseItem?.status)}`}>{caseItem?.status}</span>
                  <span className="text-muted-foreground font-body text-sm">{caseItem?.type_of_services || 'No service type'}</span>
                </div>
              </div>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="font-body" onClick={() => setStatusDialog(true)} data-testid="update-status-btn">
                <Clock className="w-4 h-4 mr-2" /> Update Status
              </Button>
              {!isEditing ? (
                <Button className="btn-lift bg-primary text-white font-body" onClick={() => setIsEditing(true)} data-testid="edit-btn">
                  <Edit2 className="w-4 h-4 mr-2" /> Edit Case
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button variant="outline" className="font-body" onClick={() => { setIsEditing(false); setEditData(caseItem); }} data-testid="cancel-edit-btn">Cancel</Button>
                  <Button className="btn-lift bg-primary text-white font-body" onClick={handleSave} disabled={saving} data-testid="save-btn">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save Changes
                  </Button>
                </div>
              )}
              <AddCustomFieldDialog
                organizationId={caseItem?.organization_id}
                onFieldAdded={fetchCustomFields}
              />
            </div>
          </div>
        </div>

        {/* AI Form Scanner — Enhanced */}
        <div className="mb-4">
          {hasFeature('form_scanner') ? (
            <EnhancedFormScanner caseId={id} onDataApplied={() => fetchCase()} />
          ) : (
            <Card className="border-dashed opacity-60" data-testid="enhanced-scanner-locked">
              <CardContent className="py-6 text-center">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Crown className="w-5 h-5 text-amber-500" />
                  <Badge className="bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/20 hover:bg-amber-500/15">Premium Feature</Badge>
                </div>
                <p className="font-body text-sm text-muted-foreground">AI Form Scanner is a premium add-on. Contact your administrator to enable this feature.</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="personal" className="space-y-4">
          <TabsList className="bg-card border border-border flex-wrap h-auto gap-1 p-1">
            <TabsTrigger value="personal" className="font-body text-xs" data-testid="tab-personal"><User className="w-3 h-3 mr-1" />Personal</TabsTrigger>
            <TabsTrigger value="contact" className="font-body text-xs" data-testid="tab-contact"><Phone className="w-3 h-3 mr-1" />Contact</TabsTrigger>
            <TabsTrigger value="immigration" className="font-body text-xs" data-testid="tab-immigration"><Globe className="w-3 h-3 mr-1" />Immigration</TabsTrigger>
            <TabsTrigger value="biographic" className="font-body text-xs" data-testid="tab-biographic"><Fingerprint className="w-3 h-3 mr-1" />Biographic</TabsTrigger>
            <TabsTrigger value="employment" className="font-body text-xs" data-testid="tab-employment"><Briefcase className="w-3 h-3 mr-1" />Employment & Education</TabsTrigger>
            <TabsTrigger value="family" className="font-body text-xs" data-testid="tab-family"><Heart className="w-3 h-3 mr-1" />Family</TabsTrigger>
            <TabsTrigger value="travel" className="font-body text-xs" data-testid="tab-travel"><Plane className="w-3 h-3 mr-1" />Travel</TabsTrigger>
            <TabsTrigger value="documents" className="font-body text-xs" data-testid="tab-documents"><FileText className="w-3 h-3 mr-1" />Documents ({documents.length})</TabsTrigger>
            <TabsTrigger value="history" className="font-body text-xs" data-testid="tab-history"><Clock className="w-3 h-3 mr-1" />History</TabsTrigger>
            <TabsTrigger value="portal" className="font-body text-xs" data-testid="tab-portal"><Link2 className="w-3 h-3 mr-1" />Portal</TabsTrigger>
          </TabsList>

          {/* ===== PERSONAL INFO TAB ===== */}
          <TabsContent value="personal" className="space-y-4">
            <Card data-testid="personal-info-card">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-base flex items-center gap-2"><User className="w-4 h-4 text-primary" />Personal Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  <F label="First Name" value={caseItem?.first_name} isEditing={isEditing} field="first_name" editData={editData} onChange={handleEditChange} />
                  <F label="Middle Name" value={caseItem?.middle_name} isEditing={isEditing} field="middle_name" editData={editData} onChange={handleEditChange} />
                  <F label="Last Name" value={caseItem?.last_name} isEditing={isEditing} field="last_name" editData={editData} onChange={handleEditChange} />
                  <F label="Maiden Name" value={caseItem?.maiden_name} isEditing={isEditing} field="maiden_name" editData={editData} onChange={handleEditChange} />
                  <F label="Title" value={caseItem?.title} isEditing={isEditing} field="title" editData={editData} onChange={handleEditChange} type="select" options={['Mr.', 'Mrs.', 'Ms.', 'Dr.', 'Prof.']} />
                  <F label="Sex" value={caseItem?.sex} isEditing={isEditing} field="sex" editData={editData} onChange={handleEditChange} type="select" options={['Male', 'Female', 'Other']} />
                  <F label="Marital Status" value={caseItem?.marital_status} isEditing={isEditing} field="marital_status" editData={editData} onChange={handleEditChange} type="select" options={['Single', 'Married', 'Divorced', 'Widowed', 'Separated']} />
                  <F label="Date of Birth" value={caseItem?.dob} isEditing={isEditing} field="dob" editData={editData} onChange={handleEditChange} type="date" />
                  <F label="City of Birth" value={caseItem?.city_of_birth} isEditing={isEditing} field="city_of_birth" editData={editData} onChange={handleEditChange} />
                  <F label="State of Birth" value={caseItem?.state_of_birth} isEditing={isEditing} field="state_of_birth" editData={editData} onChange={handleEditChange} />
                  <F label="Country of Birth" value={caseItem?.country_of_birth} isEditing={isEditing} field="country_of_birth" editData={editData} onChange={handleEditChange} />
                  <F label="Citizenship" value={caseItem?.citizenship} isEditing={isEditing} field="citizenship" editData={editData} onChange={handleEditChange} />
                  <F label="SSN" value={caseItem?.ssn} isEditing={isEditing} field="ssn" editData={editData} onChange={handleEditChange} />
                  <F label="Alien Number" value={caseItem?.a_number} isEditing={isEditing} field="a_number" editData={editData} onChange={handleEditChange} />
                  <F label="Major Field of Study" value={caseItem?.major_field_of_study} isEditing={isEditing} field="major_field_of_study" editData={editData} onChange={handleEditChange} />
                  <F label="Degree" value={caseItem?.degree} isEditing={isEditing} field="degree" editData={editData} onChange={handleEditChange} />
                  {customFields.filter(f => f.group === 'personal').map(cf => (
                    <F key={cf.id} label={cf.field_name} value={caseItem?.custom_fields?.[cf.field_key]} isEditing={isEditing} field={cf.field_key} editData={editData?.custom_fields || {}} onChange={(f, v) => handleCustomFieldChange(f, v)} />
                  ))}
                </div>
              </CardContent>
            </Card>
            {/* Other Names */}
            <ListSection title="Other Names" items={caseItem?.other_names} fields={[
              { key: 'first_name', label: 'First Name' }, { key: 'middle_name', label: 'Middle Name' }, { key: 'last_name', label: 'Last Name' }
            ]} isEditing={isEditing} listKey="other_names" editData={editData} onChange={handleEditChange} icon={BookOpen} />
          </TabsContent>

          {/* ===== CONTACT & ADDRESS TAB ===== */}
          <TabsContent value="contact" className="space-y-4">
            <Card data-testid="contact-info-card">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-base flex items-center gap-2"><Phone className="w-4 h-4 text-primary" />Contact Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  <F label="Email" value={caseItem?.email} isEditing={isEditing} field="email" editData={editData} onChange={handleEditChange} />
                  <F label="Day Phone" value={caseItem?.day_phone} isEditing={isEditing} field="day_phone" editData={editData} onChange={handleEditChange} />
                  <F label="Cell Phone" value={caseItem?.cell_phone} isEditing={isEditing} field="cell_phone" editData={editData} onChange={handleEditChange} />
                  <F label="Evening Phone" value={caseItem?.evening_phone} isEditing={isEditing} field="evening_phone" editData={editData} onChange={handleEditChange} />
                  <F label="Fax" value={caseItem?.fax} isEditing={isEditing} field="fax" editData={editData} onChange={handleEditChange} />
                  <F label="Telephone" value={caseItem?.telephone} isEditing={isEditing} field="telephone" editData={editData} onChange={handleEditChange} />
                </div>
              </CardContent>
            </Card>
            <Card data-testid="address-card">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-base flex items-center gap-2"><MapPin className="w-4 h-4 text-primary" />Addresses</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <F label="Current US Residence" value={caseItem?.address} isEditing={isEditing} field="address" editData={editData} onChange={handleEditChange} type="textarea" />
                  <F label="Mailing Address" value={caseItem?.mailing_address} isEditing={isEditing} field="mailing_address" editData={editData} onChange={handleEditChange} type="textarea" />
                  <div>
                    <p className="font-body text-xs text-muted-foreground mb-1">State</p>
                    {isEditing ? (
                      <Select value={editData.state || ''} onValueChange={(v) => {
                        handleEditChange('state', v);
                        if (editData.county) handleEditChange('county', '');
                      }}>
                        <SelectTrigger className="font-body text-sm h-9" data-testid="edit-state">
                          <SelectValue placeholder="Select state..." />
                        </SelectTrigger>
                        <SelectContent>
                          {US_STATE_OPTIONS.map(s => (
                            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="font-body text-sm text-foreground" data-testid="field-state">{caseItem?.state || '—'}</p>
                    )}
                  </div>
                  <div>
                    <p className="font-body text-xs text-muted-foreground mb-1">County</p>
                    {isEditing ? (
                      <CountyAutocomplete
                        value={editData.county || ''}
                        onChange={(val) => handleEditChange('county', val)}
                        stateAbbr={editData.state || caseItem?.state || ''}
                      />
                    ) : (
                      <p className="font-body text-sm text-foreground" data-testid="field-county">{caseItem?.county || '—'}</p>
                    )}
                  </div>
                  {customFields.filter(f => f.group === 'contact').map(cf => (
                    <F key={cf.id} label={cf.field_name} value={caseItem?.custom_fields?.[cf.field_key]} isEditing={isEditing} field={cf.field_key} editData={editData?.custom_fields || {}} onChange={(f, v) => handleCustomFieldChange(f, v)} />
                  ))}
                </div>
              </CardContent>
            </Card>
            <ListSection title="Previous Addresses" items={caseItem?.previous_addresses} fields={[
              { key: 'street', label: 'Street' }, { key: 'unit', label: 'Unit' }, { key: 'city', label: 'City' },
              { key: 'state', label: 'State' }, { key: 'zip_code', label: 'Zip Code' }, { key: 'country', label: 'Country' },
              { key: 'from', label: 'From' }, { key: 'to', label: 'To' },
            ]} isEditing={isEditing} listKey="previous_addresses" editData={editData} onChange={handleEditChange} icon={MapPin} />
          </TabsContent>

          {/* ===== IMMIGRATION TAB ===== */}
          <TabsContent value="immigration" className="space-y-4">
            <Card data-testid="immigration-status-card">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-base flex items-center gap-2"><Globe className="w-4 h-4 text-primary" />Immigration Status & Case</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  <F label="Country" value={caseItem?.country} isEditing={isEditing} field="country" editData={editData} onChange={handleEditChange} />
                  <F label="Service Type" value={caseItem?.type_of_services} isEditing={isEditing} field="type_of_services" editData={editData} onChange={handleEditChange} type="select" options={['Family Visa', 'Work Visa', 'Student Visa', 'Asylum', 'Green Card', 'Citizenship', 'DACA', 'Greencard Renewal', 'Other']} />
                  <F label="Date of Service" value={caseItem?.date_of_service} isEditing={isEditing} field="date_of_service" editData={editData} onChange={handleEditChange} type="date" />
                  <F label="Date of Arrival" value={caseItem?.date_of_arrival} isEditing={isEditing} field="date_of_arrival" editData={editData} onChange={handleEditChange} type="date" />
                  <F label="USCIS Receipt #" value={caseItem?.uscis_receipt} isEditing={isEditing} field="uscis_receipt" editData={editData} onChange={handleEditChange} />
                  <F label="Receive By" value={caseItem?.receive_by} isEditing={isEditing} field="receive_by" editData={editData} onChange={handleEditChange} />
                  <F label="USCIS Date" value={caseItem?.uscis_date} isEditing={isEditing} field="uscis_date" editData={editData} onChange={handleEditChange} type="date" />
                  <F label="USCIS Decision" value={caseItem?.uscis_decision} isEditing={isEditing} field="uscis_decision" editData={editData} onChange={handleEditChange} type="select" options={['Pending', 'Approved', 'Denied', 'RFE']} />
                  <F label="Biometrics Date" value={caseItem?.biometrics_date} isEditing={isEditing} field="biometrics_date" editData={editData} onChange={handleEditChange} type="date" />
                  <F label="Department" value={caseItem?.department} isEditing={isEditing} field="department" editData={editData} onChange={handleEditChange} />
                  <F label="Nonimmigrant Status" value={caseItem?.nonimmigrant_status} isEditing={isEditing} field="nonimmigrant_status" editData={editData} onChange={handleEditChange} />
                  <F label="NI Status Exp. Date" value={caseItem?.nonimmigrant_status_exp_date} isEditing={isEditing} field="nonimmigrant_status_exp_date" editData={editData} onChange={handleEditChange} type="date" />
                  <F label="NI Status Max Exp. Date" value={caseItem?.nonimmigrant_status_max_exp_date} isEditing={isEditing} field="nonimmigrant_status_max_exp_date" editData={editData} onChange={handleEditChange} type="date" />
                  {customFields.filter(f => f.group === 'immigration').map(cf => (
                    <F key={cf.id} label={cf.field_name} value={caseItem?.custom_fields?.[cf.field_key]} isEditing={isEditing} field={cf.field_key} editData={editData?.custom_fields || {}} onChange={(f, v) => handleCustomFieldChange(f, v)} />
                  ))}
                </div>
              </CardContent>
            </Card>
            <ListSection title="Passports" items={caseItem?.passports} fields={[
              { key: 'number', label: 'Number' }, { key: 'issuing_country', label: 'Issuing Country' },
              { key: 'issue_date', label: 'Issue Date' }, { key: 'expiration_date', label: 'Expiration Date' }, { key: 'current', label: 'Current' },
            ]} isEditing={isEditing} listKey="passports" editData={editData} onChange={handleEditChange} icon={BookOpen} />
            <ListSection title="Visas" items={caseItem?.visas} fields={[
              { key: 'number', label: 'Number' }, { key: 'issuing_post_name', label: 'Issuing Post' },
              { key: 'issue_date', label: 'Issue Date' }, { key: 'expiration_date', label: 'Expiration Date' },
              { key: 'visa_type', label: 'Visa Type/Class' }, { key: 'immigration_status', label: 'Imm. Status' }, { key: 'current', label: 'Current' },
            ]} isEditing={isEditing} listKey="visas" editData={editData} onChange={handleEditChange} icon={FileText} />
            <ListSection title="I-94s" items={caseItem?.i94s} fields={[
              { key: 'number', label: 'Number' }, { key: 'arrival_date', label: 'Arrival Date' },
              { key: 'arrival_city', label: 'Arrival City' }, { key: 'arrival_state', label: 'Arrival State' },
              { key: 'departure_date', label: 'Departure Date' }, { key: 'expiration_date', label: 'Expiration Date' }, { key: 'current', label: 'Current I-94' },
            ]} isEditing={isEditing} listKey="i94s" editData={editData} onChange={handleEditChange} icon={FileText} />
          </TabsContent>

          {/* ===== BIOGRAPHIC TAB ===== */}
          <TabsContent value="biographic">
            <Card data-testid="biographic-card">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-base flex items-center gap-2"><Fingerprint className="w-4 h-4 text-primary" />Biographic Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  <F label="Ethnicity" value={caseItem?.ethnicity} isEditing={isEditing} field="ethnicity" editData={editData} onChange={handleEditChange} type="select" options={['Hispanic or Latino', 'Not Hispanic or Latino']} />
                  <F label="Race" value={caseItem?.race} isEditing={isEditing} field="race" editData={editData} onChange={handleEditChange} type="select" options={['White', 'Black or African American', 'Asian', 'American Indian or Alaska Native', 'Native Hawaiian or Pacific Islander', 'Other']} />
                  <F label="Height" value={caseItem?.height} isEditing={isEditing} field="height" editData={editData} onChange={handleEditChange} />
                  <F label="Weight" value={caseItem?.weight} isEditing={isEditing} field="weight" editData={editData} onChange={handleEditChange} />
                  <F label="Eye Color" value={caseItem?.eye_color} isEditing={isEditing} field="eye_color" editData={editData} onChange={handleEditChange} type="select" options={['Black', 'Blue', 'Brown', 'Gray', 'Green', 'Hazel', 'Maroon', 'Pink', 'Other']} />
                  <F label="Hair Color" value={caseItem?.hair_color} isEditing={isEditing} field="hair_color" editData={editData} onChange={handleEditChange} type="select" options={['Bald', 'Black', 'Blond', 'Brown', 'Gray', 'Red', 'Sandy', 'White', 'Other']} />
                  {customFields.filter(f => f.group === 'biographic').map(cf => (
                    <F key={cf.id} label={cf.field_name} value={caseItem?.custom_fields?.[cf.field_key]} isEditing={isEditing} field={cf.field_key} editData={editData?.custom_fields || {}} onChange={(f, v) => handleCustomFieldChange(f, v)} />
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===== EMPLOYMENT & EDUCATION TAB ===== */}
          <TabsContent value="employment" className="space-y-4">
            <Card data-testid="employment-card">
              <CardHeader className="pb-3">
                <CardTitle className="font-heading text-base flex items-center gap-2"><Briefcase className="w-4 h-4 text-primary" />Employment</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  <F label="Employer Name" value={caseItem?.employer_name} isEditing={isEditing} field="employer_name" editData={editData} onChange={handleEditChange} />
                  <F label="Business Type" value={caseItem?.business_type} isEditing={isEditing} field="business_type" editData={editData} onChange={handleEditChange} />
                  <F label="Job Title" value={caseItem?.job_title} isEditing={isEditing} field="job_title" editData={editData} onChange={handleEditChange} />
                  <F label="Supervisor Name" value={caseItem?.supervisor_name} isEditing={isEditing} field="supervisor_name" editData={editData} onChange={handleEditChange} />
                  <F label="Hours Per Week" value={caseItem?.hours_per_week} isEditing={isEditing} field="hours_per_week" editData={editData} onChange={handleEditChange} />
                  <F label="Employer Email" value={caseItem?.employer_email} isEditing={isEditing} field="employer_email" editData={editData} onChange={handleEditChange} />
                  <F label="Job Description" value={caseItem?.job_description} isEditing={isEditing} field="job_description" editData={editData} onChange={handleEditChange} type="textarea" />
                  {customFields.filter(f => f.group === 'employment').map(cf => (
                    <F key={cf.id} label={cf.field_name} value={caseItem?.custom_fields?.[cf.field_key]} isEditing={isEditing} field={cf.field_key} editData={editData?.custom_fields || {}} onChange={(f, v) => handleCustomFieldChange(f, v)} />
                  ))}
                </div>
              </CardContent>
            </Card>
            <ListSection title="Previous Employment" items={caseItem?.previous_employment} fields={[
              { key: 'employer_name', label: 'Employer' }, { key: 'employer_address', label: 'Address' },
              { key: 'job_title', label: 'Job Title' }, { key: 'from', label: 'From' }, { key: 'to', label: 'To' },
            ]} isEditing={isEditing} listKey="previous_employment" editData={editData} onChange={handleEditChange} icon={Briefcase} />
            <ListSection title="Education" items={caseItem?.education} fields={[
              { key: 'school_name', label: 'School Name' }, { key: 'field', label: 'Field' }, { key: 'degree', label: 'Degree' },
              { key: 'city', label: 'City' }, { key: 'state', label: 'State' }, { key: 'country', label: 'Country' },
              { key: 'diploma_date', label: 'Diploma Date' }, { key: 'evaluated', label: 'Evaluated' },
            ]} isEditing={isEditing} listKey="education" editData={editData} onChange={handleEditChange} icon={GraduationCap} />
          </TabsContent>

          {/* ===== FAMILY TAB ===== */}
          <TabsContent value="family" className="space-y-4">
            <ListSection title="Marriages" items={caseItem?.marriages} fields={[
              { key: 'date_of_marriage', label: 'Date of Marriage' }, { key: 'marriage_city', label: 'City' },
              { key: 'marriage_state', label: 'State' }, { key: 'marriage_country', label: 'Country' },
              { key: 'date_of_termination', label: 'Termination Date' }, { key: 'termination_city', label: 'Termination City' },
            ]} isEditing={isEditing} listKey="marriages" editData={editData} onChange={handleEditChange} icon={Heart} />
            <ListSection title="Members / Relationships" items={caseItem?.members} fields={[
              { key: 'contact', label: 'Contact Name' }, { key: 'relationship', label: 'Relationship' }, { key: 'access', label: 'Case Access' },
            ]} isEditing={isEditing} listKey="members" editData={editData} onChange={handleEditChange} icon={User} />
            {customFields.filter(f => f.group === 'family').length > 0 && (
              <Card><CardContent className="pt-6"><div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {customFields.filter(f => f.group === 'family').map(cf => (
                  <F key={cf.id} label={cf.field_name} value={caseItem?.custom_fields?.[cf.field_key]} isEditing={isEditing} field={cf.field_key} editData={editData?.custom_fields || {}} onChange={(f, v) => handleCustomFieldChange(f, v)} />
                ))}
              </div></CardContent></Card>
            )}
          </TabsContent>

          {/* ===== TRAVEL TAB ===== */}
          <TabsContent value="travel">
            <ListSection title="Abroad Trips" items={caseItem?.abroad_trips} fields={[
              { key: 'countries_visited', label: 'Countries Visited' }, { key: 'purpose', label: 'Purpose' },
              { key: 'date_left_us', label: 'Date Left U.S.' }, { key: 'departure_port', label: 'Departure Port/City' },
              { key: 'date_returned', label: 'Date Returned to U.S.' },
            ]} isEditing={isEditing} listKey="abroad_trips" editData={editData} onChange={handleEditChange} icon={Plane} />
            {customFields.filter(f => f.group === 'travel').length > 0 && (
              <Card><CardContent className="pt-6"><div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {customFields.filter(f => f.group === 'travel').map(cf => (
                  <F key={cf.id} label={cf.field_name} value={caseItem?.custom_fields?.[cf.field_key]} isEditing={isEditing} field={cf.field_key} editData={editData?.custom_fields || {}} onChange={(f, v) => handleCustomFieldChange(f, v)} />
                ))}
              </div></CardContent></Card>
            )}
          </TabsContent>

          {/* ===== DOCUMENTS TAB ===== */}
          <TabsContent value="documents" className="space-y-6">
            <Card data-testid="documents-card">
              <CardHeader>
                <CardTitle className="font-heading text-base">Documents</CardTitle>
              </CardHeader>
              <CardContent>
                <div {...getRootProps()} className={cn("dropzone rounded-lg p-8 text-center cursor-pointer mb-6", isDragActive && "drag-active")} data-testid="dropzone">
                  <input {...getInputProps()} data-testid="file-input" />
                  {uploading ? <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary mb-2" /> : <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />}
                  <p className="font-body text-muted-foreground">{isDragActive ? 'Drop files here...' : 'Drag & drop files here, or click to select'}</p>
                  <p className="font-body text-sm text-muted-foreground mt-1">Supports PDF, DOC, DOCX, images</p>
                </div>
                {documents.length === 0 ? (
                  <p className="font-body text-muted-foreground text-center py-8">No documents uploaded yet</p>
                ) : (
                  <div className="space-y-3">
                    {documents.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-secondary/30 transition-colors" data-testid={`doc-${doc.id}`}>
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-muted-foreground" />
                          <div>
                            <p className="font-body font-medium text-foreground">{doc.original_name}</p>
                            <p className="font-body text-sm text-muted-foreground">{formatFileSize(doc.file_size)} &bull; {format(new Date(doc.uploaded_at), 'MMM d, yyyy')}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="ghost" size="sm" onClick={() => handleDownload(doc)} data-testid={`download-doc-${doc.id}`}><Download className="w-4 h-4" /></Button>
                          <Button variant="ghost" size="sm" className="text-error hover:text-error" onClick={() => handleDeleteDoc(doc.id)} data-testid={`delete-doc-${doc.id}`}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===== HISTORY TAB ===== */}
          <TabsContent value="history" className="space-y-6">
            <Card data-testid="history-card">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="font-heading text-base">Status History</CardTitle>
                <Button variant="outline" size="sm" onClick={() => setStatusDialog(true)} data-testid="add-status-btn"><Plus className="w-4 h-4 mr-2" /> Add Update</Button>
              </CardHeader>
              <CardContent>
                {statusUpdates.length === 0 ? (
                  <p className="font-body text-muted-foreground text-center py-8">No status updates yet</p>
                ) : (
                  <div className="space-y-4">
                    {statusUpdates.map((update, index) => (
                      <div key={update.id} className="relative pl-6 pb-4 border-l-2 border-border last:pb-0" data-testid={`status-${update.id}`}>
                        <div className={cn("absolute left-0 top-0 w-3 h-3 rounded-full -translate-x-[7px]", index === 0 ? "bg-primary" : "bg-muted")} />
                        <div>
                          <span className={`px-2 py-0.5 rounded text-xs font-body font-medium ${getStatusColor(update.status)}`}>{update.status}</span>
                          {update.notes && <p className="font-body text-foreground mt-2">{update.notes}</p>}
                          <p className="font-body text-sm text-muted-foreground mt-1">by {update.created_by} &bull; {format(new Date(update.created_at), 'MMM d, yyyy h:mm a')}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ===== CLIENT PORTAL TAB ===== */}
          <TabsContent value="portal" className="space-y-6">
            <Card data-testid="client-portal-card">
              <CardHeader>
                <CardTitle className="font-heading text-base flex items-center gap-2"><Link2 className="w-4 h-4 text-primary" />Client Portal Access</CardTitle>
                <CardDescription className="font-body">Give your client access to view their case, upload documents, and fill out forms.</CardDescription>
              </CardHeader>
              <CardContent>
                {clientPortal && clientPortal.is_active !== false ? (
                  <div className="space-y-6">
                    <div className="flex items-center gap-2 p-3 bg-success/10 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-success" />
                      <span className="font-body text-sm font-medium text-success">Portal is active</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="p-4 bg-secondary/50 rounded-lg">
                        <Label className="font-body text-xs text-muted-foreground uppercase tracking-wider">Client Email</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="font-body font-medium text-foreground">{clientPortal.email}</p>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => copyToClipboard(clientPortal.email)}><Copy className="w-3 h-3" /></Button>
                        </div>
                      </div>
                      <div className="p-4 bg-secondary/50 rounded-lg">
                        <Label className="font-body text-xs text-muted-foreground uppercase tracking-wider">Portal URL</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <p className="font-body text-sm font-medium text-foreground truncate">{clientPortal.portal_url}</p>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => copyToClipboard(clientPortal.portal_url)}><Copy className="w-3 h-3" /></Button>
                        </div>
                      </div>
                    </div>
                    {newAccessCode && (
                      <div className="p-4 bg-accent/10 border border-accent/30 rounded-lg">
                        <Label className="font-body text-xs text-accent uppercase tracking-wider">Access Code (Share with client)</Label>
                        <div className="flex items-center gap-3 mt-2">
                          <p className="font-mono text-2xl font-bold text-accent tracking-widest">{newAccessCode}</p>
                          <Button variant="outline" size="sm" onClick={() => copyToClipboard(newAccessCode)}><Copy className="w-4 h-4 mr-1" /> Copy</Button>
                        </div>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-3">
                      <Button variant="outline" className="font-body" onClick={handleRegenerateCode} disabled={portalLoading} data-testid="regenerate-code-btn">
                        {portalLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />} Generate New Code
                      </Button>
                      <Button variant="outline" className="font-body" onClick={() => window.open(clientPortal.portal_url, '_blank')} data-testid="preview-portal-btn">
                        <ExternalLink className="w-4 h-4 mr-2" /> Preview Portal
                      </Button>
                      <Button variant="outline" className="font-body text-error hover:bg-error/10" onClick={handleRevokePortal} disabled={portalLoading} data-testid="revoke-portal-btn">
                        <Trash2 className="w-4 h-4 mr-2" /> Revoke Access
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4"><Link2 className="w-8 h-8 text-primary" /></div>
                    <h3 className="font-heading text-lg font-semibold text-foreground mb-2">No Client Portal</h3>
                    <p className="font-body text-muted-foreground mb-6 max-w-md mx-auto">Create a portal to allow {caseItem?.name || 'your client'} to view their case, upload docs, and fill forms.</p>
                    <Button className="btn-lift bg-primary text-white font-body" onClick={() => { setPortalEmail(caseItem?.email || ''); setPortalDialog(true); }} data-testid="create-portal-btn">
                      <Plus className="w-4 h-4 mr-2" /> Create Client Portal
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Create Portal Dialog */}
      <Dialog open={portalDialog} onOpenChange={setPortalDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Create Client Portal</DialogTitle>
            <DialogDescription className="font-body">Enter the client's email address to create their portal access.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="font-body">Client Email</Label>
              <Input type="email" placeholder="client@example.com" value={portalEmail} onChange={(e) => setPortalEmail(e.target.value)} className="font-body" data-testid="portal-email-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPortalDialog(false)} className="font-body">Cancel</Button>
            <Button onClick={handleCreatePortal} disabled={portalLoading || !portalEmail} className="bg-primary text-white font-body" data-testid="confirm-create-portal-btn">
              {portalLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Key className="w-4 h-4 mr-2" />} Create Portal
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Update Dialog */}
      <Dialog open={statusDialog} onOpenChange={setStatusDialog}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Update Case Status</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="font-body">New Status</Label>
              <Select value={newStatus.status} onValueChange={(v) => setNewStatus(prev => ({ ...prev, status: v }))}>
                <SelectTrigger className="font-body" data-testid="new-status-select"><SelectValue placeholder="Select status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Pending">Pending</SelectItem>
                  <SelectItem value="Approved">Approved</SelectItem>
                  <SelectItem value="Denied">Denied</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="font-body">Notes (optional)</Label>
              <Textarea value={newStatus.notes} onChange={(e) => setNewStatus(prev => ({ ...prev, notes: e.target.value }))} placeholder="Add notes..." className="font-body" rows={3} data-testid="status-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusDialog(false)} className="font-body" data-testid="cancel-status-btn">Cancel</Button>
            <Button onClick={handleAddStatus} className="bg-primary text-white font-body" data-testid="save-status-btn">Save Status</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
